import { Accordion as MantineAccordion, Collapse as MantineCollapse, Spoiler as MantineSpoiler, Tabs as MantineTabs } from "@mantine/core";
import * as React from "react";

import type { ElementProps, SizeValue, StyleProps } from "../primitives/style-props";

/**
 * Disclosure composites: Tabs and Accordion (SC-2699).
 *
 * Both carry controlled and uncontrolled state, and both drive arrow-key navigation entirely
 * through DOM contracts the vendor owns — Tabs through `[role="tab"]` inside `[role="tablist"]`,
 * Accordion through `[data-accordion-control]` inside `[data-accordion]`, each filtered to the
 * same ancestor level. These wrappers spread onto the vendor root and add no element of their own:
 * a wrapper that failed to render the vendor root — so `[role="tablist"]` / `[data-accordion]` is
 * absent — would kill keyboard navigation with no type error and no other failing test.
 * `composites/disclosure-dom.test.tsx` pins every key direction and both state modes for that.
 */

/** Tabs value contract: the active tab's `value`, or null when none is active. */
export type TabsValue = string | null;
export type TabsOrientation = "horizontal" | "vertical";
/**
 * Restated with the vendor's open arm intact: Mantine types this as `Variants<'default' | 'outline'
 * | 'pills'>`, which is that union `| (string & {})` so a theme can register its own. Narrowing to
 * the bare union would be a divergence from the vendor rather than a port of it — the same reason
 * DatePickerInput's `type` is restated widened. Spelled the way SizeValue is in style-props.ts.
 */
export type TabsVariant = "default" | "outline" | "pills" | (string & Record<never, never>);
export type TabsPosition = "left" | "center" | "right" | "apart";

export interface TabsProps extends Omit<ElementProps<"div">, "defaultValue">, StyleProps {
  children: React.ReactNode;
  /** Active tab for a controlled Tabs; pair with `onTabChange`. */
  value?: TabsValue;
  /** Initial active tab for an uncontrolled Tabs. */
  defaultValue?: TabsValue;
  /**
   * Owned name, kept at the Mantine 6 spelling. Mantine 7 renames this to `onChange`; absorbing
   * that rename is this wrapper's job, so the 16 call sites never change — the same decision
   * `leftIcon`/`rightIcon` record in controls/README.md.
   */
  onTabChange?(value: TabsValue): void;
  orientation?: TabsOrientation;
  variant?: TabsVariant;
  /** Keeps inactive panel content mounted; Mantine's own default is true. */
  keepMounted?: boolean;
}

/**
 * Mantine 7 spends the `onChange` name on the tab value (`Tabs.mjs`), where Mantine 6 left it the
 * root div's DOM handler. The owned `onTabChange` takes the vendor callback, and the root is rendered
 * here through Mantine's `renderRoot` so the owned DOM `onChange` still reaches the div (SC-2703).
 */
const TabsBase = React.forwardRef<HTMLDivElement, TabsProps>(function Tabs({ children, onTabChange, onChange, ...props }, ref) {
  return (
    <MantineTabs
      {...props}
      onChange={onTabChange}
      renderRoot={rootProps => <div {...rootProps} onChange={onChange} />}
      data-sc-print-hidden="true"
      ref={ref}
    >
      {children}
    </MantineTabs>
  );
});

export interface TabsListProps extends ElementProps<"div">, StyleProps {
  children: React.ReactNode;
  /** Stretches the tabs to fill the list. */
  grow?: boolean;
  position?: TabsPosition;
}

/**
 * Mantine 7 replaced Tabs.List's `position` with a CSS `justify`; these are the values Mantine 6's
 * `GROUP_POSITIONS` gave each position (`Group.styles.js`), so the owned name keeps its layout.
 */
const TABS_LIST_JUSTIFY: Record<TabsPosition, React.CSSProperties["justifyContent"]> = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
  apart: "space-between",
};

const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(function TabsList({ children, position, ...props }, ref) {
  return (
    <MantineTabs.List {...props} justify={position && TABS_LIST_JUSTIFY[position]} ref={ref}>
      {children}
    </MantineTabs.List>
  );
});

export interface TabsTabProps extends Omit<ElementProps<"button">, "value">, StyleProps {
  /** Connects the tab to its panel of the same value. */
  value: string;
  children?: React.ReactNode;
  /** Owned name at the Mantine 6 spelling, renamed to `leftSection` in v7. */
  icon?: React.ReactNode;
}

const TabsTab = React.forwardRef<HTMLButtonElement, TabsTabProps>(function TabsTab({ children, icon, ...props }, ref) {
  return (
    <MantineTabs.Tab {...props} leftSection={icon} ref={ref}>
      {children}
    </MantineTabs.Tab>
  );
});

export interface TabsPanelProps extends ElementProps<"div">, StyleProps {
  children: React.ReactNode;
  /** Connects the panel to its tab of the same value. */
  value: string;
}

const TabsPanel = React.forwardRef<HTMLDivElement, TabsPanelProps>(function TabsPanel({ children, ...props }, ref) {
  return (
    <MantineTabs.Panel {...props} ref={ref}>
      {children}
    </MantineTabs.Panel>
  );
});

export const Tabs = Object.assign(TabsBase, {
  List: TabsList,
  Tab: TabsTab,
  Panel: TabsPanel,
});

export type AccordionVariant = "default" | "contained" | "filled" | "separated";
export type AccordionChevronPosition = "left" | "right";

/**
 * Generic over `multiple`, exactly as Mantine types it: `multiple` flips the value contract
 * between a single open item and a list of them. Restated rather than simplified because five
 * call sites use the `multiple` arm (two of them in Navbar, backed by a string[] store) and eight
 * use the single arm. Same treatment as ChipGroup in controls/toggles.tsx.
 */
export type AccordionValue<Multiple extends boolean = false> = Multiple extends true ? string[] : string | null;

export interface AccordionProps<Multiple extends boolean = false>
  extends Omit<ElementProps<"div">, "onChange" | "defaultValue">, StyleProps {
  children: React.ReactNode;
  /** Allows several items open at once, and widens the value contract to a list. */
  multiple?: Multiple;
  /** Open item(s) for a controlled Accordion; pair with `onChange`. */
  value?: AccordionValue<Multiple>;
  /** Initially open item(s) for an uncontrolled Accordion. */
  defaultValue?: AccordionValue<Multiple>;
  onChange?(value: AccordionValue<Multiple>): void;
  variant?: AccordionVariant;
  chevronPosition?: AccordionChevronPosition;
  radius?: SizeValue;
  /**
   * Classes for Accordion's inner elements. `className` reaches only the root, so without these a
   * consumer has to restyle through the vendor's `styles` prop, which is Mantine 6's styling API
   * and is reshaped in v7. Routing through owned names keeps that change inside src/ui. Same
   * owned-name-forwarding-one-class shape as Alert's `wrapperClassName`/`bodyClassName`.
   *
   * These sit on the root rather than on Item/Control/Panel because Mantine resolves the whole
   * slot map from the Accordion root — which is also where all three call sites passed `styles`.
   */
  itemClassName?: string;
  controlClassName?: string;
  labelClassName?: string;
  contentClassName?: string;
}

function AccordionBase<Multiple extends boolean = false>({
  children,
  itemClassName,
  controlClassName,
  labelClassName,
  contentClassName,
  ...props
}: AccordionProps<Multiple>): React.JSX.Element {
  const classNames =
    itemClassName || controlClassName || labelClassName || contentClassName
      ? { item: itemClassName, control: controlClassName, label: labelClassName, content: contentClassName }
      : undefined;

  return (
    <MantineAccordion<Multiple> {...props} classNames={classNames}>
      {children}
    </MantineAccordion>
  );
}

export interface AccordionItemProps extends ElementProps<"div">, StyleProps {
  /** Identifies the item in the Accordion's value. */
  value: string;
  children?: React.ReactNode;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(function AccordionItem({ children, ...props }, ref) {
  return (
    <MantineAccordion.Item {...props} data-sc-accordion-item="true" ref={ref}>
      {children}
    </MantineAccordion.Item>
  );
});

export interface AccordionControlProps extends ElementProps<"button">, StyleProps {
  children?: React.ReactNode;
  /** Unlike Tabs.Tab and Menu.Item, Accordion.Control keeps the `icon` name in Mantine 7. */
  icon?: React.ReactNode;
  /** Replaces the chevron for this control only. */
  chevron?: React.ReactNode;
}

const AccordionControl = React.forwardRef<HTMLButtonElement, AccordionControlProps>(function AccordionControl({ children, ...props }, ref) {
  return (
    <MantineAccordion.Control {...props} ref={ref}>
      {children}
    </MantineAccordion.Control>
  );
});

/**
 * `onTransitionEnd` is omitted because Mantine omits it too: the panel forwards to Collapse, whose
 * `onTransitionEnd` is the collapse-animation callback rather than the DOM event handler, so the
 * two contracts collide under the same name. No call site passes it.
 */
export interface AccordionPanelProps extends Omit<ElementProps<"div">, "onTransitionEnd">, StyleProps {
  children?: React.ReactNode;
}

/** Mantine renders the panel as a plain function component, so there is no ref to forward. */
const AccordionPanel = ({ children, ...props }: AccordionPanelProps): React.JSX.Element => (
  <MantineAccordion.Panel {...props}>{children}</MantineAccordion.Panel>
);

export const Accordion = Object.assign(AccordionBase, {
  Item: AccordionItem,
  Control: AccordionControl,
  Panel: AccordionPanel,
});

/**
 * Shows a fixed-height preview until its control is activated. The labels and height stay
 * required because every existing caller supplies both; a hidden default would be application
 * policy rather than a vendor-faithful boundary.
 */
export interface SpoilerProps extends ElementProps<"div">, StyleProps {
  children?: React.ReactNode;
  maxHeight: number;
  hideLabel: React.ReactNode;
  showLabel: React.ReactNode;
  controlRef?: React.ForwardedRef<HTMLButtonElement>;
  initialState?: boolean;
  transitionDuration?: number;
}

export const Spoiler = React.forwardRef<HTMLDivElement, SpoilerProps>(function Spoiler(props, ref) {
  return <MantineSpoiler {...props} ref={ref} />;
});

export interface CollapseProps extends Omit<ElementProps<"div">, "onTransitionEnd">, StyleProps {
  in: boolean;
}

/**
 * Mantine 6 removed a closed Collapse from layout. Mantine 7 leaves its zero-height root in layout,
 * so this composite owns the close-completion state needed to restore the prior behavior.
 */
export const Collapse = React.forwardRef<HTMLDivElement, CollapseProps>(function Collapse({ children, in: opened, style, ...props }, ref) {
  const [closed, setClosed] = React.useState(!opened);

  React.useEffect(() => {
    if (opened) {
      setClosed(false);
    }
  }, [opened]);

  return (
    <MantineCollapse
      {...props}
      in={opened}
      style={closed && !opened ? { ...style, display: "none" } : style}
      onTransitionEnd={() => {
        if (!opened) {
          setClosed(true);
        }
      }}
      ref={ref}
    >
      {children}
    </MantineCollapse>
  );
});
